/*
  PDP modules: offer countdown, delivery promise and pack picker behaviour.
  Loaded once with `defer` by sections/main-product.liquid. Everything is delegated or driven by
  data attributes, so blocks re-rendered by the theme (variant change) keep working without
  re-running inline scripts.
*/
(function () {
  'use strict';
  if (window.DVFPdp) return;

  var IST_OFFSET_MS = 330 * 60 * 1000; // India has no DST, so a fixed +05:30 is exact.
  var GEO_KEY = 'dvf:geo';
  var GEO_TTL_MS = 24 * 60 * 60 * 1000;
  var GEO_FAIL_TTL_MS = 60 * 60 * 1000; // the free API rate-limits; don't retry on every page view
  var GEO_TIMEOUT_MS = 4000;

  // ---------------------------------------------------------------- countdown

  // Next occurrence of HH:MM (India time) after `now`, as epoch ms.
  function nextDailyEnd(hhmm, now) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
    if (!m) return null;
    var h = Math.min(23, parseInt(m[1], 10));
    var min = Math.min(59, parseInt(m[2], 10));
    var ist = new Date(now + IST_OFFSET_MS); // read with getUTC* to get India wall-clock fields
    var end = Date.UTC(ist.getUTCFullYear(), ist.getUTCMonth(), ist.getUTCDate(), h, min) - IST_OFFSET_MS;
    if (end <= now) end += 24 * 60 * 60 * 1000;
    return end;
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  // Remaining ms -> { h, m, s } with hours uncapped (a 3-day offer shows 72 hr).
  function splitTime(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    return { h: pad(Math.floor(total / 3600)), m: pad(Math.floor(total / 60) % 60), s: pad(total % 60) };
  }

  function countdownEnd(el, now) {
    if (el.dataset.daily) return nextDailyEnd(el.dataset.daily, now);
    var end = parseInt(el.dataset.end, 10);
    return isNaN(end) ? null : end;
  }

  var ticker = null;

  function paintCountdowns() {
    var now = Date.now();
    var live = 0;
    document.querySelectorAll('[data-pdp-countdown]').forEach(function (el) {
      var end = countdownEnd(el, now);
      if (end === null || end <= now) {
        el.hidden = true;
        return;
      }
      var t = splitTime(end - now);
      el.querySelector('[data-cd-h]').textContent = t.h;
      el.querySelector('[data-cd-m]').textContent = t.m;
      el.querySelector('[data-cd-s]').textContent = t.s;
      el.hidden = false;
      live++;
    });
    if (!live) stopTicker();
  }

  function startTicker() {
    if (ticker || document.hidden) return;
    paintCountdowns();
    if (document.querySelector('[data-pdp-countdown]:not([hidden])')) ticker = setInterval(paintCountdowns, 1000);
  }

  function stopTicker() {
    clearInterval(ticker);
    ticker = null;
  }

  // ---------------------------------------------------------------- delivery promise

  // "delhi, gurgaon = Delhi NCR" lines -> { delhi: 'Delhi NCR', gurgaon: 'Delhi NCR' }
  function parseCities(text) {
    var map = {};
    String(text || '').split(/\n|;/).forEach(function (line) {
      var parts = line.split('=');
      var label = (parts[1] || '').trim();
      parts[0].split(',').forEach(function (c) {
        var city = c.trim().toLowerCase();
        if (city) map[city] = label || c.trim();
      });
    });
    return map;
  }

  // Longest matching key wins, so "greater noida" beats "noida" and "new delhi" beats "delhi".
  function matchCity(city, map) {
    city = String(city || '').toLowerCase().trim();
    if (!city) return null;
    var best = null;
    Object.keys(map).forEach(function (key) {
      if (city.indexOf(key) !== -1 && (!best || key.length > best.length)) best = key;
    });
    return best ? map[best] : null;
  }

  // Cached lookup: a city string, false for a recent failure, or null when there is nothing fresh.
  function readGeoCache() {
    try {
      var c = JSON.parse(localStorage.getItem(GEO_KEY) || 'null');
      if (!c) return null;
      if (c.err) return Date.now() - c.t < GEO_FAIL_TTL_MS ? false : null;
      if (typeof c.city === 'string' && Date.now() - c.t < GEO_TTL_MS) return c.city;
    } catch (e) {}
    return null;
  }

  function writeGeoCache(entry) {
    try {
      entry.t = Date.now();
      localStorage.setItem(GEO_KEY, JSON.stringify(entry));
    } catch (e) {}
  }

  var geoPromise = null;

  // One lookup per visitor per day, shared by every delivery block on the page.
  function lookupCity() {
    if (geoPromise) return geoPromise;
    var cached = readGeoCache();
    if (cached === false) return (geoPromise = Promise.reject(new Error('geo lookup failed recently')));
    if (cached !== null) return (geoPromise = Promise.resolve(cached));
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl && setTimeout(function () { ctrl.abort(); }, GEO_TIMEOUT_MS);
    geoPromise = fetch('https://ipwho.is/?fields=success,city', ctrl ? { signal: ctrl.signal } : {})
      .then(function (r) { return r.json(); })
      .then(function (d) {
        clearTimeout(timer);
        if (!d || d.success === false) throw new Error('geo lookup failed');
        var city = String(d.city || '');
        writeGeoCache({ city: city });
        return city;
      });
    geoPromise.catch(function () {
      clearTimeout(timer);
      writeGeoCache({ err: 1 });
    });
    return geoPromise;
  }

  function showDelivery(el, region) {
    var r = el.querySelector('[data-dp-region]');
    if (r && region) r.textContent = '— ' + region;
    el.hidden = false;
  }

  function initDelivery(el) {
    if (el.dataset.ready) return;
    el.dataset.ready = '1';
    if (el.dataset.mode === 'always') return showDelivery(el, el.dataset.fallback);
    var map = parseCities(el.dataset.cities);
    lookupCity().then(
      function (city) {
        var region = matchCity(city, map);
        if (region) showDelivery(el, region);
        else el.hidden = true;
      },
      function () {
        if (el.dataset.showOnError === 'true') showDelivery(el, el.dataset.fallback);
      }
    );
  }

  // ---------------------------------------------------------------- pack picker

  function onPackChange(event) {
    var radio = event.target;
    if (!radio.classList || !radio.classList.contains('ps-radio')) return;
    var grid = radio.closest('.ps-grid');
    if (!grid) return;
    grid.querySelectorAll('.ps-card.is-on').forEach(function (c) { c.classList.remove('is-on'); });
    if (radio.nextElementSibling) radio.nextElementSibling.classList.add('is-on');
  }

  var prefetched = {};
  function onPackHover(event) {
    var link = event.target.closest && event.target.closest('a.ps-card');
    if (!link || !link.href || prefetched[link.href]) return;
    prefetched[link.href] = true;
    var tag = document.createElement('link');
    tag.rel = 'prefetch';
    tag.href = link.href;
    document.head.appendChild(tag);
  }

  // ---------------------------------------------------------------- wiring

  function init() {
    document.querySelectorAll('[data-pdp-delivery]').forEach(initDelivery);
    stopTicker();
    startTicker();
  }

  document.addEventListener('change', onPackChange, true);
  document.addEventListener('pointerover', onPackHover, { passive: true });
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stopTicker();
    else startTicker();
  });
  // Theme editor: blocks are re-rendered in place when settings change.
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:block:select', init);

  // Picking a pack that is a different product swaps the whole <product-rerender> element.
  // Watching only its parent's direct children keeps this observer silent otherwise.
  function watchRerenders() {
    if (typeof MutationObserver !== 'function') return;
    var mo = new MutationObserver(init);
    document.querySelectorAll('product-rerender').forEach(function (el) {
      if (el.parentNode) mo.observe(el.parentNode, { childList: true });
    });
  }

  function boot() {
    init();
    watchRerenders();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  window.DVFPdp = {
    nextDailyEnd: nextDailyEnd,
    splitTime: splitTime,
    parseCities: parseCities,
    matchCity: matchCity,
    lookupCity: lookupCity,
    paintCountdowns: paintCountdowns,
    isTicking: function () { return ticker !== null; },
    init: init
  };
})();
